/// <reference path="./vite-env.d.ts" />
import { easingFor, springEasing, timingFor } from './animation';
import type { BuildEndPayload, BuildErrorPayload, BuildStartPayload } from './devEvents';
import { ChromeColor } from './chromeColor';
import { frameFor, installDeviceFrame } from './deviceFrame';
import { applyPageMode, decidePageMode, installViewportReadout, readViewportInsets, watchKeyboard, watchViewport, type PageMode } from './deviceMode';
import { EMPTY_CATALOG, catalogPath, isAssetCatalog, type AssetCatalog } from './assets';
import { createMockApp } from './mock';
import {
  horizontalSizeClassFor,
  isDynamicTypeSize,
  type ColorScheme,
  type DynamicTypeSize,
  type HorizontalSizeClass,
  type RendererEvent,
  type ScenePhase,
} from './protocol';
import { BrowserHistory } from './browserHistory';
import { Renderer, RendererError } from './renderer';
import { withBlockedHosts } from './blockedHosts';
import { withCORSProxy } from './corsProxy';
import { browserEnvironment, installPushService } from './hostRequests';
import { locateBundleFiles } from './media';
import { currentScenePhase } from './scenePhase';
import { relayUndo } from './undo';
import { callbackPageURL } from './webAuth';
import { bitmapStore } from './bitmaps';
import { DEFAULT_TYPE_SIZE, applyTypeSizeVariables } from './typography';
import { JobScheduler, loadApp, type AppHandle, type JobTimers } from './wasm';
import { chooseLocale, currentTimeZone } from './locale';
import { indexedDBPersistence, launchValue, type SwiftDataPersistence, type SwiftDataStores } from './swiftData';
import { applyHomeChanges, diffHome, homeDirectory, homeEntries, indexedDBFiles, type FilesPersistence, type HomeEntries } from './appFiles';
import type { BundleManifest } from '../plugins/bundle-resources';

const THEME_KEY = 'sb-theme';
const TYPE_SIZE_KEY = 'sb-type-size';
type Theme = ColorScheme;

function $<T extends HTMLElement>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`Missing element ${selector}`);
  return el;
}

const device = $<HTMLElement>('#device');
const screen = $<HTMLElement>('#screen');
const appName = $<HTMLElement>('#app-name');
const themeToggle = $<HTMLButtonElement>('#theme-toggle');
const typeSizeSelect = $<HTMLSelectElement>('#type-size');
const status = $<HTMLElement>('#status');
const buildStatus = $<HTMLElement>('#build-status');

const params = new URLSearchParams(location.search);
const useMock = params.get('mock') === '1';
// The app: `?app=`, else the site's default (`define`, node/config.ts).
const requestedApp = (params.get('app') ?? __SB_DEFAULT_APP__).replace(/[^A-Za-z0-9_-]/g, '') || __SB_DEFAULT_APP__;

// Frame mode (the fake iPhone bezel, desktop and tests) or device mode (a
// real phone: full screen, real safe areas, OS status bar; every screen of a
// site built for a deployment). See deviceMode.ts; the inline script in
// app/index.html pre-set the attribute with the same rule.
const pageMode: PageMode = decidePageMode(location.search, window, __SB_PAGE_MODE__);
applyPageMode(pageMode);
const deviceMode = pageMode === 'device';

// ---------------------------------------------------------------------------
// Renderer (constructed first: the theme and type size controls feed it)
// ---------------------------------------------------------------------------

let app: AppHandle | null = null;
/** The app's asset catalog manifest (empty until `boot` fetched it, or when the app has none). */
let assets: AssetCatalog = EMPTY_CATALOG;
/** JSON events handed to the app, newest last (debugging and e2e tests). */
const sentEvents: RendererEvent[] = [];
/**
 * Events the renderer produced before the app handle existed (the `geometry`
 * events of the very first layout pass, which runs inside `loadApp` /
 * `createMockApp`); delivered right after boot.
 */
let queuedEvents: RendererEvent[] = [];

function dispatch(event: RendererEvent): void {
  if (!app) {
    queuedEvents.push(event);
    return;
  }
  try {
    if (event.type === 'tap' && event.x === undefined) {
      app.sendEvent(event.id);
    } else if (event.type === 'batch' && !app.supportsBatchEvents) {
      // The mock apps handle events one at a time.
      event.events.forEach(dispatch);
    } else if (app.sendEventJson(event)) {
      // Recorded flattened, so tests see the individual events.
      if (event.type === 'batch') sentEvents.push(...event.events);
      else sentEvents.push(event);
    }
  } catch (error) {
    showError(error);
  }
}

const renderer = new Renderer(screen, dispatch);

// The browser's back pops the app's screens and dismisses its sheets (browserHistory.ts).
new BrowserHistory(renderer, screen).enable();

// On a phone the browser's status bar strip and safe areas take the color the
// app draws at its top edge (chromeColor.ts).
const chromeColor = deviceMode ? new ChromeColor(device, screen) : null;
if (chromeColor) renderer.afterLayout = () => chromeColor.schedule();

// Phase 12: `.preferredColorScheme` from the app overrides the viewer's theme
// for this page (not persisted); `null` goes back to the toggle / OS setting.
screen.addEventListener('sb:color-scheme', (event) => {
  const scheme = (event as CustomEvent<{ colorScheme: 'light' | 'dark' | null }>).detail.colorScheme;
  if (scheme === 'light' || scheme === 'dark') applyTheme(scheme, false);
  else applyTheme(deviceMode ? systemTheme() : (readStoredTheme() ?? 'light'), false);
});

// Text measured before web fonts finished loading may be off; lay out again.
if (typeof document.fonts?.ready?.then === 'function') {
  document.fonts.ready.then(() => renderer.fontsChanged()).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

function readStoredTheme(): Theme | null {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function currentTheme(): Theme {
  return device.dataset['theme'] === 'dark' ? 'dark' : 'light';
}

function applyTheme(theme: Theme, persist: boolean): void {
  device.dataset['theme'] = theme;
  themeToggle.setAttribute('aria-pressed', String(theme === 'dark'));
  themeToggle.textContent = theme === 'dark' ? 'Light mode' : 'Dark mode';
  renderer.setEnvironment({ colorScheme: theme });
  chromeColor?.schedule();
  if (persist) {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Storage may be unavailable (private mode); the toggle still works.
    }
  }
}

/**
 * The OS appearance: device mode follows it, and an embedded page
 * (`?embed=1`, the playground's simulator) too; the toolbar toggle is
 * hidden in both.
 */
const darkScheme = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
const systemTheme = (): Theme => (darkScheme?.matches ? 'dark' : 'light');

if (deviceMode || document.documentElement.hasAttribute('data-sb-embed')) {
  applyTheme(systemTheme(), false);
  darkScheme?.addEventListener('change', () => {
    applyTheme(systemTheme(), false);
    sendEnvironment();
  });
} else {
  applyTheme(readStoredTheme() ?? 'light', false);
}
themeToggle.addEventListener('click', () => {
  applyTheme(currentTheme() === 'dark' ? 'light' : 'dark', true);
  sendEnvironment();
});

// ---------------------------------------------------------------------------
// Dynamic Type
// ---------------------------------------------------------------------------

function readStoredTypeSize(): DynamicTypeSize | null {
  try {
    const value = localStorage.getItem(TYPE_SIZE_KEY);
    return isDynamicTypeSize(value) ? value : null;
  } catch {
    return null;
  }
}

function currentTypeSize(): DynamicTypeSize {
  const value = device.dataset['typeSize'];
  return isDynamicTypeSize(value) ? value : DEFAULT_TYPE_SIZE;
}

/**
 * Select a Dynamic Type size: the device records it (`data-type-size`), the
 * text style CSS variables are rewritten so styled text rescales at once,
 * and Swift is told through an `environment` event (by the caller).
 */
function applyTypeSize(size: DynamicTypeSize, persist: boolean): void {
  device.dataset['typeSize'] = size;
  applyTypeSizeVariables(device, size, renderer.platform);
  // The layout engine resolves text styles for this size and re-lays out.
  renderer.setEnvironment({ dynamicTypeSize: size });
  if (typeSizeSelect.value !== size) typeSizeSelect.value = size;
  if (persist) {
    try {
      localStorage.setItem(TYPE_SIZE_KEY, size);
    } catch {
      // Storage may be unavailable; the control still works for the session.
    }
  }
}

// Device mode has no Text size control and always reports `large`.
applyTypeSize(deviceMode ? DEFAULT_TYPE_SIZE : (readStoredTypeSize() ?? DEFAULT_TYPE_SIZE), false);
typeSizeSelect.addEventListener('change', () => {
  const value = typeSizeSelect.value;
  applyTypeSize(isDynamicTypeSize(value) ? value : DEFAULT_TYPE_SIZE, true);
  sendEnvironment();
});

// ---------------------------------------------------------------------------
// Device mode: real viewport and safe areas
// ---------------------------------------------------------------------------

/**
 * Phase 7: the horizontal size class Swift sees (`regular` from 700 CSS px of
 * screen width). The iPhone frame is compact, the iPad frame regular, and
 * without a frame (or in device mode) it follows the window.
 */
function currentSizeClass(): HorizontalSizeClass {
  return horizontalSizeClassFor(screen.clientWidth);
}
/** The size class last sent in an `environment` event (null before the first). */
let sentSizeClass: HorizontalSizeClass | null = null;
/** The platform last sent in an `environment` event (null before the first). */
let sentPlatform: string | null = null;

if (deviceMode) {
  // `#screen` fills the viewport (styles.css); the renderer re-reads its size
  // when it resizes. The safe areas come from env(safe-area-inset-*): non-zero
  // only in standalone (home screen) mode or landscape, which is what the
  // real app would get.
  renderer.setSafeArea(readViewportInsets());
  let lastSize = `${screen.clientWidth}x${screen.clientHeight}`;
  watchViewport(() => {
    renderer.setSafeArea(readViewportInsets());
    const size = `${screen.clientWidth}x${screen.clientHeight}`;
    if (size !== lastSize) {
      lastSize = size;
      renderer.viewportChanged();
    }
    // A rotation or window resize may cross the compact / regular threshold.
    if (sentSizeClass !== null && currentSizeClass() !== sentSizeClass) sendEnvironment();
  });
  // The on-screen keyboard does not shrink `#screen` (the layout viewport
  // keeps its height); it covers the screen's bottom. The engine gets its
  // height as a bottom inset, so a field or a chat's composer rises above
  // it and a scroll view keeps its end in view, as on iOS.
  // `?debug=viewport` shows the numbers the measurement reads, over the app.
  const readout = new URLSearchParams(location.search).get('debug') === 'viewport' ? installViewportReadout() : undefined;
  watchKeyboard((height) => renderer.setKeyboard(height), { root: device, onRead: readout });
} else if (!document.documentElement.hasAttribute('data-sb-embed')) {
  // A desktop preview: the toolbar's Device picker (deviceFrame.ts) draws the
  // app in an iPhone, an iPad or no device; the renderer re-reads the
  // screen's size when it changes, and Swift hears a new size class.
  const sizeClassMayHaveChanged = (): void => {
    // The Mac's text styles are its own: the device's variables follow the platform.
    applyTypeSizeVariables(device, currentTypeSize(), renderer.platform);
    if (sentSizeClass !== null && (currentSizeClass() !== sentSizeClass || renderer.platform !== sentPlatform)) sendEnvironment();
  };
  // An app built for macOS (docs/notes/macos.md) starts in a Mac window.
  installDeviceFrame(renderer, { onChange: sizeClassMayHaveChanged, preferred: frameFor(registeredApp()?.platform) });
  watchViewport(sizeClassMayHaveChanged);
}

// ---------------------------------------------------------------------------
// Phase 8: scene phase
// ---------------------------------------------------------------------------

/** The scene phase last sent in an `environment` event (null before the first). */
let sentScenePhase: ScenePhase | null = null;

const scenePhaseMayHaveChanged = (): void => {
  if (sentScenePhase !== null && currentScenePhase() !== sentScenePhase) sendEnvironment();
};
document.addEventListener('visibilitychange', scenePhaseMayHaveChanged);
window.addEventListener('focus', scenePhaseMayHaveChanged);
window.addEventListener('blur', scenePhaseMayHaveChanged);

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

/**
 * Tell Swift which color scheme, Dynamic Type size and (Phase 7) horizontal
 * size class the device shows (no-op for Phase 1 modules; Phase 2 modules
 * ignore `dynamicTypeSize`, pre-Phase 7 modules the size class).
 */
function sendEnvironment(): void {
  if (!app?.supportsJsonEvents) return;
  const horizontalSizeClass = currentSizeClass();
  sentSizeClass = horizontalSizeClass;
  // Phase 8: every environment event carries the scene phase.
  const scenePhase = currentScenePhase();
  sentScenePhase = scenePhase;
  // The platform the renderer draws (docs/notes/macos.md).
  const platform = renderer.platform;
  sentPlatform = platform;
  dispatch({
    type: 'environment',
    colorScheme: currentTheme(),
    dynamicTypeSize: currentTypeSize(),
    horizontalSizeClass,
    scenePhase,
    platform,
  });
}

/** Deliver the events queued while the app was loading (after `environment`). */
function flushQueuedEvents(): void {
  const queued = queuedEvents;
  queuedEvents = [];
  for (const event of queued) dispatch(event);
}

function showError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  status.textContent = message;
  status.hidden = false;
  device.dataset['state'] = 'error';
  console.error(error);
}

function clearError(): void {
  status.hidden = true;
  status.textContent = '';
  if (device.dataset['state'] === 'error') device.dataset['state'] = app ? 'ready' : 'loading';
}

function setLoaded(name: string): void {
  appName.textContent = name;
  status.hidden = true;
  device.dataset['state'] = 'ready';
}

/**
 * Phase 5: the app's asset catalog manifest (`/catalog/<App>.json`, see
 * plugins/asset-catalogs.ts). An app without a catalog gets a 404, and a
 * missing or broken manifest must never keep the app from loading, so every
 * failure yields the empty catalog.
 */
// ---------------------------------------------------------------------------
// Phase 11: locale, time zone, saved defaults and the resource bundle
// ---------------------------------------------------------------------------

const defaultsKey = `sb-defaults:${requestedApp}`;

/** The saved `UserDefaults` of this app, as the JSON the module reads from `SB_DEFAULTS`. */
function readStoredDefaults(): string | null {
  try {
    const text = localStorage.getItem(defaultsKey);
    if (text === null) return null;
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? text : null;
  } catch {
    return null;
  }
}

function storeDefaults(values: Record<string, unknown>): void {
  try {
    if (Object.keys(values).length === 0) localStorage.removeItem(defaultsKey);
    else localStorage.setItem(defaultsKey, JSON.stringify(values));
  } catch (error) {
    console.warn('[sb] could not store UserDefaults', error);
  }
}

/** The app's entry in the registry (`locales`), when the page knows it. */
function registeredApp(): SBLandingApp | undefined {
  try {
    return __SB_APPS__.find((entry: SBLandingApp) => entry.name === requestedApp);
  } catch {
    return undefined;
  }
}

/**
 * The WASI environment for this launch: the locale the app runs in (the
 * viewer's language when the module carries its data, `?locale=` to override),
 * the viewer's time zone, the saved defaults and the app's name.
 */
function launchEnvironment(): string[] {
  const env: string[] = [`SB_APP_NAME=${requestedApp}`];
  const locale = chooseLocale(navigator.languages ?? [navigator.language], registeredApp()?.locales, params.get('locale'));
  if (locale) env.push(`SB_LOCALE=${locale}`);
  const zone = currentTimeZone();
  if (zone) env.push(`TZ=${zone}`);
  const defaults = readStoredDefaults();
  if (defaults !== null) env.push(`SB_DEFAULTS=${defaults}`);
  // Signing in: the app's URL schemes (its Info.plist's CFBundleURLTypes) and
  // the callback page its OAuth redirects become (webAuth.ts).
  const schemes = registeredApp()?.urlSchemes;
  if (schemes?.length) env.push(`SB_URL_SCHEMES=${schemes.join(',')}`);
  env.push(`SB_WEB_AUTH_CALLBACK=${callbackPageURL(new URL(import.meta.env.BASE_URL, location.href).href)}`);
  // Web Push (notifications.ts): the service worker next to the page, and
  // the key `registerForRemoteNotifications` subscribes with.
  installPushService(`${import.meta.env.BASE_URL}sw.js`, import.meta.env.BASE_URL);
  const webPushKey = registeredApp()?.webPushKey;
  if (webPushKey) env.push(`SB_WEB_PUSH_KEY=${webPushKey}`);
  return env;
}

// ---------------------------------------------------------------------------
// Phase 18: SwiftData's stores, kept in IndexedDB
// ---------------------------------------------------------------------------

/** The app's database, or null where the browser has no IndexedDB (or forbids it). */
const swiftData: SwiftDataPersistence | null = (() => {
  try {
    return typeof indexedDB === 'undefined' ? null : indexedDBPersistence(requestedApp);
  } catch {
    return null;
  }
})();

/**
 * Every SwiftData store the app saved, for `SB_SWIFTDATA`. `?swiftdata=reset`
 * deletes them first (a model changed in a way the saved records cannot
 * follow). A database that cannot be read starts the app empty.
 */
async function loadSwiftData(): Promise<SwiftDataStores> {
  if (!swiftData) return {};
  try {
    if (params.get('swiftdata') === 'reset') await swiftData.clear();
    return await swiftData.load();
  } catch (error) {
    console.warn('[sb] could not read the SwiftData stores; starting empty', error);
    return {};
  }
}

// ---------------------------------------------------------------------------
// Phase 19: the app's home directory, kept in IndexedDB
// ---------------------------------------------------------------------------

const files: FilesPersistence | null = (() => {
  try {
    return typeof indexedDB === 'undefined' ? null : indexedDBFiles(requestedApp);
  } catch {
    return null;
  }
})();

/** The home as last written to IndexedDB, to diff the live tree against. */
let savedHome: HomeEntries = new Map();

/** The saved home (`?files=reset` deletes it first); empty when it cannot be read. */
async function loadHome(): Promise<HomeEntries> {
  if (!files) return new Map();
  try {
    if (params.get('files') === 'reset') await files.clear();
    return await files.load();
  } catch (error) {
    console.warn('[sb] could not read the app\'s files; starting empty', error);
    return new Map();
  }
}

/** Writes what the app changed in its home since the last write. */
function saveHome(home: ReturnType<typeof homeDirectory>): void {
  const changes = diffHome(savedHome, homeEntries(home));
  if (changes.put.length === 0 && changes.remove.length === 0) return;
  savedHome = applyHomeChanges(savedHome, changes);
  files?.apply(changes).catch((error: unknown) => console.warn('[sb] could not save the app\'s files to IndexedDB', error));
}

/** Where the page serves the resource at `path` (inside the bundle) of `app`. */
function bundleFileURL(app: string, path: string): string {
  return `${import.meta.env.BASE_URL}bundle/${app}/${path.split('/').map(encodeURIComponent).join('/')}`;
}

/** The app's resources (`/bundle/<App>.json` and its files), for the WASI `/bundle` mount; empty without a manifest. */
async function loadBundle(app: string): Promise<Map<string, Uint8Array>> {
  const files = new Map<string, Uint8Array>();
  try {
    const response = await fetch(`${import.meta.env.BASE_URL}bundle/${app}.json`, { cache: 'no-cache' });
    if (!response.ok) return files;
    const manifest = (await response.json()) as BundleManifest;
    if (!Array.isArray(manifest.files)) return files;
    await Promise.all(
      manifest.files.map(async (file) => {
        const data = await fetch(bundleFileURL(app, file.path), { cache: 'no-cache' });
        if (data.ok) files.set(file.path, new Uint8Array(await data.arrayBuffer()));
        else console.warn(`[sb] resource ${file.path} of ${app}: HTTP ${data.status}`);
      }),
    );
  } catch (error) {
    console.warn(`[sb] could not load the resources of ${app}`, error);
  }
  return files;
}

async function loadAssetCatalog(app: string): Promise<AssetCatalog> {
  try {
    // The manifest and its files are written at the site's root paths; a site
    // served under a base path (`/native/`) has them under it.
    const base = import.meta.env.BASE_URL;
    const under = (path: string): string => (base !== '/' && path.startsWith('/') ? `${base.replace(/\/$/, '')}${path}` : path);
    const response = await fetch(under(catalogPath(app)), { cache: 'no-cache' });
    if (!response.ok) return EMPTY_CATALOG;
    const json: unknown = await response.json();
    if (!isAssetCatalog(json)) return EMPTY_CATALOG;
    return { ...json, images: Object.fromEntries(Object.entries(json.images).map(([name, image]) => [name, { ...image, src: under(image.src) }])) };
  } catch {
    return EMPTY_CATALOG;
  }
}

// ---------------------------------------------------------------------------
// Hot reload: state snapshot across full reloads
// ---------------------------------------------------------------------------
//
// Only the dev server's page (`import.meta.hot`) keeps state across a reload:
// a built site starts fresh, as an app does at launch.

const stateKey = `sb-state:${requestedApp}`;

/** Store the app's `@State` snapshot so the next boot can restore it. */
function stashState(): void {
  if (!app || useMock) return;
  try {
    const snapshot = app.snapshotState();
    if (snapshot) sessionStorage.setItem(stateKey, snapshot);
    else sessionStorage.removeItem(stateKey);
  } catch (error) {
    console.warn('[sb] could not snapshot state', error);
  }
}

/** Read and delete a stashed snapshot (it is only ever used once). */
function takeStashedState(): string | null {
  if (!import.meta.hot) return null;
  try {
    const snapshot = sessionStorage.getItem(stateKey);
    if (snapshot !== null) sessionStorage.removeItem(stateKey);
    return snapshot;
  } catch {
    return null;
  }
}

if (import.meta.hot) {
  window.addEventListener('beforeunload', stashState);
  window.addEventListener('pagehide', stashState);
  import.meta.hot.on('vite:beforeFullReload', () => stashState());
  import.meta.hot.on('sb:build-start', (payload: BuildStartPayload) => {
    buildStatus.textContent = `Rebuilding ${payload.apps.join(', ')}…`;
    buildStatus.dataset['state'] = 'building';
    buildStatus.hidden = false;
  });
  import.meta.hot.on('sb:build-end', (payload: BuildEndPayload) => {
    if (payload.ok) {
      buildStatus.textContent = `Built ${payload.apps.join(', ')} in ${(payload.durationMs / 1000).toFixed(1)}s`;
      buildStatus.dataset['state'] = 'ok';
      clearError();
      // A full reload follows; the indicator is hidden again on the fresh page.
    } else {
      buildStatus.textContent = 'Build failed';
      buildStatus.dataset['state'] = 'error';
    }
  });
  import.meta.hot.on('sb:build-error', (payload: BuildErrorPayload) => {
    // Keep the page usable: the running app stays up, the banner shows why
    // the new build did not land.
    status.textContent = `Build failed for ${payload.app}:\n${payload.output.trim()}`;
    status.hidden = false;
    console.error(`[sb] build failed for ${payload.app}\n${payload.output}`);
  });
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

async function boot(): Promise<void> {
  const onOps = (ops: Parameters<Renderer['applyOps']>[0]): void => {
    try {
      renderer.applyOps(ops);
    } catch (error) {
      if (error instanceof RendererError) showError(error);
      throw error;
    }
  };

  // The catalog comes first: the first render pass already needs image sizes
  // and colors. The mock app has no catalog (the fetch yields the empty one).
  assets = await loadAssetCatalog(requestedApp);
  renderer.setAssetCatalog(assets);

  if (useMock) {
    app = createMockApp(onOps, requestedApp);
    setLoaded(`${requestedApp} (mock)`);
    sendEnvironment();
    flushQueuedEvents();
    return;
  }

  // The module the page runs: the app's, or one compiled in this browser
  // (`?module=` a blob: URL, which only this origin can make; the playground
  // page passes its compiler's output this way).
  const compiled = params.get('module');
  const url = compiled?.startsWith('blob:') ? compiled : `${import.meta.env.BASE_URL}${requestedApp}.wasm`;
  appName.textContent = `Loading ${requestedApp}…`;
  device.dataset['state'] = 'loading';
  const env = launchEnvironment();
  const restored = takeStashedState();
  if (restored !== null) {
    env.push(`SB_STATE=${restored}`);
    console.info(`[sb] restoring state for ${requestedApp}: ${restored}`);
  }
  const bundle = await loadBundle(requestedApp);
  // A player given a bundle file streams it from here rather than through a copy of its bytes (src/media.ts).
  locateBundleFiles((path) => (bundle.has(path) ? bundleFileURL(requestedApp, path) : null));
  const stores = launchValue(await loadSwiftData());
  if (stores !== null) env.push(`SB_SWIFTDATA=${stores}`);
  savedHome = await loadHome();
  const home = homeDirectory(savedHome);
  // The standard directories a new home gets are saved with its first write.
  const corsProxy = registeredApp()?.corsProxy;
  app = await loadApp(url, onOps, {
    // `?arg=` (repeatable) adds launch arguments after the app's name, as
    // `simctl launch <app> <args>` does: CI's iOS fidelity workflow opens a
    // screen of Examples/Fidelity with `?arg=-screen&arg=<name>`.
    args: [requestedApp, ...params.getAll('arg')],
    env,
    bundle,
    home,
    onFilesWritten: () => saveHome(home),
    onDefaults: storeDefaults,
    // `ShareLink` shares asset-catalog images as their files. An app built
    // with a CORS proxy retries a refused cross-origin read through it
    // (corsProxy.ts); one built with blocked hosts never reaches them (blockedHosts.ts).
    requests: {
      ...browserEnvironment,
      fetch: withBlockedHosts(
        corsProxy ? withCORSProxy(browserEnvironment.fetch, corsProxy, location.origin) : browserEnvironment.fetch,
        registeredApp()?.blockedHosts ?? [],
      ),
      assetURL: (name) => assets.images[name]?.src,
      bitmapFile: (recipe) => bitmapStore.png(recipe),
      // The app's undo manager reports its state in `undo` requests; the
      // keyboard's undo and redo go back to it as events (undo.ts).
      ...(browserEnvironment.undoManager ? { undoManager: relayUndo(browserEnvironment.undoManager, dispatch) } : {}),
    },
    onSwiftData: (op) => {
      swiftData?.apply(op).catch((error: unknown) => console.warn('[sb] could not save to IndexedDB', error));
    },
  });
  setLoaded(requestedApp);
  sendEnvironment();
  flushQueuedEvents();
}

boot().catch(showError);

// Debug handle for the console and e2e tests.
declare global {
  interface Window {
    __sb: {
      renderer: Renderer;
      app: () => AppHandle | null;
      /** `frame` (fake iPhone bezel) or `device` (full screen on a real phone). */
      mode: PageMode;
      /** Non-tap events that reached the app through the JSON channel. */
      sentEvents: readonly RendererEvent[];
      /** Current device appearance, as sent in `environment` events. */
      colorScheme: () => ColorScheme;
      /** Current Dynamic Type size, as sent in `environment` events. */
      typeSize: () => DynamicTypeSize;
      /** Phase 7: the horizontal size class of `#screen` (`regular` from 700 CSS px), as sent in `environment` events. */
      sizeClass: () => HorizontalSizeClass;
      /** Phase 8: the scene phase from the document's visibility and focus, as sent in `environment` events. */
      scenePhase: () => ScenePhase;
      /** Current `@State` snapshot (null for mocks without one / Phase 1 modules). */
      snapshotState: () => string | null;
      /** Phase 5: the asset catalog manifest the renderer resolves named images and colors with. */
      assets: () => AssetCatalog;
      /** Animation → CSS timing helpers (src/animation.ts), for inspection and tests. */
      animation: { easingFor: typeof easingFor; timingFor: typeof timingFor; springEasing: typeof springEasing };
      /**
       * Phase 4: run the app's executor now (`sb_run_jobs`), apply its ops and
       * reschedule. Returns the delay until the next timer, or null when the
       * module (or mock) has no executor.
       */
      runJobs: () => number | null;
      /** Phase 19: the app's home as IndexedDB holds it now: path → text of each file (`null` for a directory). */
      files: () => Promise<Record<string, string | null>>;
      /** Phase 18: the app's SwiftData stores as IndexedDB holds them now (all saves written). */
      swiftData: () => Promise<SwiftDataStores>;
      /** The executor driver class, so tests can run it against a stub. */
      JobScheduler: new (
        run: (nowMs: number) => number,
        apply: () => void,
        timers?: JobTimers,
        onError?: (error: unknown) => void,
      ) => JobScheduler;
    };
  }
}
window.__sb = {
  renderer,
  app: () => app,
  mode: pageMode,
  sentEvents,
  colorScheme: currentTheme,
  typeSize: currentTypeSize,
  sizeClass: currentSizeClass,
  scenePhase: currentScenePhase,
  snapshotState: () => app?.snapshotState() ?? null,
  assets: () => assets,
  animation: { easingFor, timingFor, springEasing },
  runJobs: () => app?.runJobs() ?? null,
  swiftData: async () => (swiftData ? swiftData.load() : {}),
  files: async () => {
    const entries = files ? await files.load() : new Map<string, Uint8Array | null>();
    return Object.fromEntries([...entries].map(([path, data]) => [path, data === null ? null : new TextDecoder().decode(data)]));
  },
  JobScheduler,
};
